import { useMemo, useState } from 'react';

import { Checkbox } from '~/components/form';
import { Button, Modal, ModalActions, SearchBar } from '~/components/ui';
import { matchesProviderModelSearch } from '~/lib/ai/listFilters';
import { summarizeAddModelsOutcomes, type AddModelsSummary } from '~/lib/ai/addModelsResult';
import { apiErrorMessage, type ApiErrorInput } from '~/lib/apiError';
import {
  useAddConfiguredModelMutation,
  useGetConfiguredModelsQuery,
  useGetProvidersQuery,
} from '~/modules/query/ai/providers';

interface AddModelsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called once after every selected model was tried; the page shows the outcome. */
  onDone: (summary: AddModelsSummary) => void;
}

/** Pick models from the provider catalog that are not configured yet and add them. */
export function AddModelsModal({ isOpen, onClose, onDone }: AddModelsModalProps) {
  const { data: catalog, isLoading, error } = useGetProvidersQuery(undefined, { skip: !isOpen });
  const { data: configured } = useGetConfiguredModelsQuery();
  const [addModel] = useAddConfiguredModelMutation();
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const available = useMemo(() => {
    const configuredIds = new Set((configured?.data ?? []).map((model) => model.id));
    return (catalog?.models ?? []).filter((model) => !configuredIds.has(model.id));
  }, [catalog?.models, configured?.data]);

  const rows = useMemo(
    () => available.filter((model) => matchesProviderModelSearch(model, q)),
    [available, q],
  );

  const close = () => {
    setQ('');
    setSelected([]);
    onClose();
  };

  const toggle = (modelId: string, checked: boolean) => {
    setSelected((previous) =>
      checked ? [...previous, modelId] : previous.filter((id) => id !== modelId),
    );
  };

  const addSelected = async () => {
    setBusy(true);

    const settled = await Promise.allSettled(
      selected.map((modelId) => addModel({ modelId }).unwrap()),
    );
    const summary = summarizeAddModelsOutcomes(
      settled.map((result, index) => ({
        modelId: selected[index],
        errorMessage:
          result.status === 'fulfilled'
            ? null
            : (apiErrorMessage(result.reason as ApiErrorInput) ??
              `${selected[index]} could not be added. Try again.`),
      })),
    );

    setBusy(false);
    close();
    onDone(summary);
  };

  let body: React.ReactNode;

  if (isLoading) {
    body = <p className="text-sm text-gray-500 dark:text-gray-400">Loading models…</p>;
  } else if (error && !catalog) {
    body = (
      <p role="alert" className="text-sm text-error-600 dark:text-error-500">
        The provider's models could not be loaded. Close this and try again.
      </p>
    );
  } else if (available.length === 0) {
    body = (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Every model the provider offers is already configured.
      </p>
    );
  } else {
    body = (
      <div className="space-y-3">
        <SearchBar
          value={q}
          onChange={setQ}
          placeholder="Search name, ID or owner…"
          className="w-full"
          testId="ai-settings-models-add-search"
        />
        {rows.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No models match your search.</p>
        ) : (
          <ul
            className="max-h-96 space-y-1 overflow-y-auto"
            data-testid="ai-settings-models-add-list"
          >
            {rows.map((model) => (
              <li key={model.id}>
                <Checkbox
                  id={`ai-settings-models-add-${model.id}`}
                  testId={`ai-settings-models-add-${model.id}`}
                  checked={selected.includes(model.id)}
                  disabled={busy}
                  onChange={(checked) => toggle(model.id, checked)}
                  text={
                    <span>
                      <span className="font-medium text-gray-800 dark:text-white/90">
                        {model.name}
                      </span>{' '}
                      <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                        {model.id}
                      </span>
                      {model.ownedBy ? (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {' '}
                          · {model.ownedBy}
                        </span>
                      ) : null}
                    </span>
                  }
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      busy={busy}
      size="default"
      title="Add models"
      description="Choose the provider models this workspace can use. New models start active."
      testId="ai-settings-models-add-modal"
      footer={
        <ModalActions
          onDismiss={close}
          dismissLabel="Cancel"
          dismissDisabled={busy}
          dismissTestId="ai-settings-models-add-cancel"
        >
          <Button
            size="sm"
            loading={busy}
            disabled={selected.length === 0}
            onClick={() => {
              void addSelected();
            }}
            testId="ai-settings-models-add-submit"
          >
            {`Add selected (${selected.length})`}
          </Button>
        </ModalActions>
      }
    >
      {body}
    </Modal>
  );
}
